import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { customFetcher } from './customFetcher';

export interface MeetingAuditUser {
    id: number;
    displayName: string;
}

export interface Meeting {
    id: number;
    name: string;
    room_name: string;
    is_active: boolean;
    created_at: string;
    created_by: MeetingAuditUser | null;
}

export const meetingsQueryKey = ['meetings'] as const;
export const jitsiConfigQueryKey = ['meetings', 'config'] as const;

export const useMeetings = () =>
    useQuery<Meeting[], Error>({
        queryKey: meetingsQueryKey,
        refetchOnWindowFocus: true,
        refetchInterval: 15000,
        queryFn: async () => customFetcher<Meeting[]>({ endpoint: '/meetings/' }),
        initialData: [],
    });

export const useJitsiConfig = () =>
    useQuery<{ domain: string }, Error>({
        queryKey: jitsiConfigQueryKey,
        staleTime: Infinity,
        queryFn: async () => customFetcher<{ domain: string }>({ endpoint: '/meetings/config/' }),
    });

// Kurzlebiges Jitsi-JWT für genau dieses Meeting (nur eingeloggte User bekommen eines).
export const useMeetingToken = (id: number | null) =>
    useQuery<{ jwt: string | null }, Error>({
        queryKey: ['meetings', 'token', id],
        enabled: id !== null,
        staleTime: 3 * 60 * 1000,
        queryFn: async () => customFetcher<{ jwt: string | null }>({ endpoint: `/meetings/${id}/token/` }),
    });

export const useCreateMeeting = () => {
    const queryClient = useQueryClient();
    return useMutation<Meeting, Error, { name: string }>({
        mutationFn: async ({ name }) =>
            customFetcher<Meeting>({ endpoint: '/meetings/', method: 'POST', body: { name } }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: meetingsQueryKey });
        },
    });
};

export const useEndMeeting = () => {
    const queryClient = useQueryClient();
    return useMutation<Meeting, Error, { id: number }>({
        mutationFn: async ({ id }) =>
            customFetcher<Meeting>({ endpoint: `/meetings/${id}/end/`, method: 'POST' }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: meetingsQueryKey });
        },
    });
};
